import React, { useCallback, useMemo } from 'react';
import {
  BetResponseCode,
  convertAmount,
  freeBetStore,
  FreeBetWinningType,
  getAmountWithCurrency,
  notificationStore,
  useTranslation,
} from '@pascal/pascal-core';
import { applicationStore, gameConfigStore } from '@pascal/pascal-core/shared/core/store';
import { observer } from 'mobx-react-lite';
import { doBetStore, gameStore } from '../../../../store';
import { autoBetDataKeys, autoCashOutDataKeys, globalTranslationKeys } from '../../../../enums';
import { minus, multiply, plus, toFixed } from '../../../../helpers/arithmeticOperations';

const BetButton = observer(({ decidedCallBackCall }) => {
  const t = useTranslation();
  const { Bet, CashOut, BetForNextRound, Cancel } = globalTranslationKeys;
  const {
    betId,
    isNextRoundBet,
    isBetAccepted,
    isDisabled,
    amount,
    freeBetId,
    freeBetMinOdd,
    freeBetType,
    freeBetMaxPayout = null,
  } = doBetStore.betData;
  const { isAutoBet, autoBetCount, isOpened } = doBetStore.autoBetData;
  const { isBetsClosed, oddData } = gameStore;
  const { totalBetsAmount } = doBetStore;
  const { activeFreeBet } = freeBetStore;
  const {
    limits: { maxBet },
  } = gameConfigStore;
  const { isDemo } = applicationStore;
  const { InValidMaxBet } = BetResponseCode;

  const betButtonContent = useMemo(() => {
    return isBetAccepted
      ? CashOut
      : !isBetAccepted && isBetsClosed && !isNextRoundBet
      ? BetForNextRound
      : !isBetAccepted && isNextRoundBet && isBetsClosed
      ? Cancel
      : Bet;
  }, [isBetAccepted, isNextRoundBet, isBetsClosed]);

  const activeClassName = useMemo(() => {
    if (isAutoBet && (autoBetCount ?? 0)) {
      if (isBetAccepted) {
        return CashOut;
      }
      return Cancel;
    }
    return betButtonContent;
  }, [isAutoBet, autoBetCount, isBetsClosed, isBetAccepted, isOpened, isNextRoundBet]);

  const handleBetButtonEvents = useCallback(() => {
    if (!isBetAccepted && !isBetsClosed) {
      doBetStore.doBetSender();
    } else if (isBetAccepted) {
      doBetStore.sendCashOut(betId);
    } else if (!isBetAccepted && isBetsClosed) {
      doBetStore.handleBetForNextRound();
    }
  }, [isBetAccepted, isBetsClosed, isAutoBet, isBetsClosed]);

  const disabled = (isBetAccepted && !isBetsClosed) || isDisabled ? 'passive' : '';

  const isDisabledWithAutoBet =
    isAutoBet && autoBetCount && activeClassName === Cancel ? '' : disabled;

  const activeClass =
    isAutoBet && autoBetCount && isBetAccepted
      ? CashOut
      : isAutoBet && autoBetCount && isBetAccepted
      ? Cancel
      : !isAutoBet && !autoBetCount
      ? betButtonContent
      : Bet;

  const cashOutAmount =
    freeBetId && freeBetType === FreeBetWinningType.Win
      ? toFixed(
          minus(
            Number(toFixed(multiply(gameStore.oddData || 0, Number(amount)), 2)),
            amount || 0
          ) || 0,
          2
        )
      : toFixed(multiply(gameStore.oddData || 0, amount || 0) || 0, 2);

  const currentAmount =
    freeBetId && freeBetMaxPayout && cashOutAmount > freeBetMaxPayout
      ? toFixed(freeBetMaxPayout, 2)
      : cashOutAmount > gameConfigStore.limits.maxWin
      ? gameConfigStore.limits.maxWin
      : cashOutAmount;

  // Todo add with-freebet class on button when freebet is active
  return (
    <button
      type='button'
      className={`bet-big-button ${
        (isBetAccepted && oddData && freeBetId && freeBetMinOdd && freeBetMinOdd > oddData) ||
        (!oddData && isBetsClosed)
          ? 'passive'
          : ''
      } ${isDisabledWithAutoBet} ${
        (!isAutoBet && !autoBetCount && activeClass === BetForNextRound) ||
        activeClassName === BetForNextRound
          ? Bet.toLowerCase()
          : activeClassName.toLowerCase()
      } ${
        freeBetId &&
        !activeFreeBet.isMinOddNull &&
        (activeClassName !== Cancel || activeClassName !== Cancel) &&
        isBetAccepted &&
        oddData
          ? 'with-freebet'
          : ''
      }`}
      onClick={() => {
        if (
          isDemo &&
          plus(Number(amount), Number(totalBetsAmount)) > Number(maxBet) &&
          !isBetAccepted
        ) {
          notificationStore.setNotificationType({ responseCode: InValidMaxBet });
        } else {
          if (!doBetStore.autoCashOutData.isValidatedOdd) {
            decidedCallBackCall(() => {
              if (isAutoBet && autoBetCount && activeClassName !== CashOut) {
                doBetStore.updateAutoBetData(autoBetDataKeys.isAutoBet, false);
                doBetStore.updateAutoBetData(autoBetDataKeys.autoBetCount, null);
                doBetStore.updateAutoBetData(autoBetDataKeys.isOpened, false);
                doBetStore.updateIsDisabledBetSection(false);
              } else {
                handleBetButtonEvents();
              }
            }, true);
          } else {
            doBetStore.updateAutoCashOutData(autoCashOutDataKeys.isValidatedOdd, false);
          }
        }
      }}
    >
      {/* add/ bet,cancel,cashout,passive class */}
      {isBetAccepted && gameStore.oddData && (
        <span
          className={`cashout-coef-block ${
            isBetAccepted && oddData && freeBetId && freeBetMinOdd && freeBetMinOdd > oddData
              ? 'coef-block-inactive'
              : ''
          }`}
        >
          <span className='cashout-coef-block-txt'>
            {amount &&
              gameStore.oddData &&
              `${getAmountWithCurrency({
                amount: convertAmount(toFixed(currentAmount, 2)),
              })} `}
          </span>
        </span>
      )}
      <span className='big-bet-btn-inner' title={t(activeClassName)}>
        <span className='big-bet-btn-title'>
          {activeFreeBet?.id && activeClassName === Bet
            ? `${t(globalTranslationKeys.FreeBet)} ${activeFreeBet?.remainingCount}`
            : t(activeClassName)}
        </span>

        {freeBetId &&
        !activeFreeBet.isMinOddNull &&
        (activeClassName !== Cancel || activeClassName !== Cancel) ? (
          <span className='bet-section-btn-info'>
            {`${t(globalTranslationKeys?.MinOdd)} x${freeBetMinOdd} `}
          </span>
        ) : (
          <></>
        )}
      </span>
    </button>
  );
});

export default BetButton;
